import { Injectable, inject, OnDestroy, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Silent, automated PWA update system:
 * - Boot from cache (instant 0ms startup).
 * - Stage new deployments in the background.
 * - Auto-apply silently when the tab is hidden / screen is locked.
 * - Auto-apply seamlessly on the next route navigation (e.g. clicking Orders, Settlements).
 * - Auto-apply when idle on the current page (if not actively editing a form).
 * - Zero banners, zero buttons, zero user prompts.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService implements OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private readonly router = inject(Router);
  private sub?: Subscription;
  private checkTimer?: ReturnType<typeof setInterval>;
  private idleApplyTimer?: ReturnType<typeof setTimeout>;
  private pendingVersion = false;
  private navSwapArmed = false;
  private visibilityHandler?: () => void;

  readonly isUpdating = signal<boolean>(false);

  constructor() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.sub = new Subscription();

    // 1. New version downloaded and ready in cache
    this.sub.add(
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.pendingVersion = true;
          this.scheduleAutoApply();
        }),
    );

    // 2. Unrecoverable state handler (cache corrupted or broken)
    this.sub.add(
      this.swUpdate.unrecoverable.subscribe((evt) => {
        console.warn('Service Worker unrecoverable state detected:', evt.reason);
        document.location.reload();
      }),
    );

    // 3. Auto-apply on route navigation
    this.sub.add(
      this.router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe((e) => {
        if (!this.navSwapArmed) {
          this.navSwapArmed = true;
          return;
        }
        if (!this.pendingVersion || this.isUpdating()) return;
        if (this.isUserInForm()) return;
        void this.applyUpdate(e.url);
      }),
    );

    // Initial check on boot
    this.swUpdate.checkForUpdate().catch(() => undefined);

    // 4. Check on tab visibility / auto-apply on tab hidden
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (!this.swUpdate.isEnabled) return;
        if (document.visibilityState === 'hidden') {
          // Tab minimized or phone locked — silent reload in background
          if (this.pendingVersion && !this.isUpdating() && !this.isUserInForm()) {
            void this.applyUpdate();
          }
          return;
        }
        // Tab foregrounded — check for any new deployment
        this.swUpdate.checkForUpdate().catch(() => undefined);
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    // Periodic check every 60 seconds
    if (typeof window !== 'undefined') {
      this.checkTimer = window.setInterval(() => {
        this.swUpdate.checkForUpdate().catch(() => undefined);
      }, 60_000);
    }
  }

  /**
   * Schedules a gentle automatic apply when the user is idle on the page
   */
  private scheduleAutoApply(): void {
    if (this.idleApplyTimer) {
      clearTimeout(this.idleApplyTimer);
    }

    // If tab is already hidden, apply immediately
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      if (!this.isUserInForm()) {
        void this.applyUpdate();
        return;
      }
    }

    // If user is on-screen, wait a brief quiet period (4 seconds).
    // If not typing in a form, apply automatically.
    this.idleApplyTimer = setTimeout(() => {
      if (!this.pendingVersion || this.isUpdating()) return;
      if (this.isUserInForm()) {
        // Retry in 10s if user was actively typing
        this.scheduleAutoApply();
        return;
      }
      void this.applyUpdate();
    }, 4000);
  }

  async applyUpdate(nextUrl?: string): Promise<void> {
    if (this.isUpdating()) return;
    this.isUpdating.set(true);

    if (this.idleApplyTimer) {
      clearTimeout(this.idleApplyTimer);
    }

    if (!this.swUpdate.isEnabled) {
      this.reloadTo(nextUrl);
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
    } catch (err) {
      console.warn('Failed to activate service worker update:', err);
    } finally {
      this.reloadTo(nextUrl);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
    }
    if (this.idleApplyTimer) {
      clearTimeout(this.idleApplyTimer);
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  private isUserInForm(): boolean {
    if (typeof document === 'undefined') return false;
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return !!el.closest('form');
  }

  private reloadTo(nextUrl?: string): void {
    if (nextUrl && typeof document !== 'undefined') {
      document.location.assign(nextUrl);
      return;
    }
    document.location.reload();
  }
}
